/**
 * CategoryDetail — /c/:slug
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Hash, MessageCircle, MapPin, ArrowRight } from "lucide-react";
import FollowButton from "@/components/FollowButton";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/categories`;
const CP_API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/chatpulse/v1`;

export default function CategoryDetail() {
  const { slug } = useParams();
  const [cat, setCat] = useState(null);
  const [threads, setThreads] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setCat(null);
    setNotFound(false);
    (async () => {
      const r = await fetch(`${API}/${slug}`);
      if (r.status === 404) { setNotFound(true); return; }
      const c = await r.json();
      setCat(c);
      const t = await fetch(`${CP_API}/timeline?category=${slug}&limit=20`).then(r => r.json());
      setThreads(t.threads || []);
    })();
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="category-not-found">
        <Hash className="w-8 h-8 text-slate-300 mx-auto" />
        <p className="mt-3 text-sm text-slate-600">Category not found.</p>
        <Link to="/categories" className="text-blue-600 hover:underline text-sm font-semibold mt-2 inline-block">All categories →</Link>
      </div>
    );
  }
  if (!cat) return <div className="max-w-3xl mx-auto px-4 py-16 text-center text-sm text-slate-400" data-testid="category-loading">Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 lg:px-6 py-6" data-testid="category-detail">
      <div className="flex items-center gap-1 text-xs text-slate-500 mb-3">
        <Link to="/categories" className="hover:text-blue-700 uppercase tracking-widest font-semibold">Categories</Link>
        <span className="text-slate-300">›</span>
        <span className="text-slate-900 font-semibold">{cat.label}</span>
      </div>

      <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900">{cat.label}</h1>
      <p className="text-sm text-slate-600 mt-1 max-w-xl">{cat.description}</p>

      <div className="mt-4 flex flex-wrap gap-2">
        {(cat.example_topics || []).map((t) => (
          <span key={t} className="text-[11px] px-3 py-1 rounded-full bg-slate-100 border border-slate-200 text-slate-700">{t}</span>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <span className="text-xs text-slate-500">{cat.discussions_count} discussions</span>
        <Link to={`/thread/new?category=${cat.slug}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700" data-testid="category-start-btn">
          <MessageCircle className="w-3 h-3" /> Start a {cat.label} discussion
        </Link>
        <FollowButton targetType="category" targetSlug={cat.slug} />
      </div>

      {cat.top_places && cat.top_places.length > 0 && (
        <section className="mt-8" data-testid="category-top-places">
          <h2 className="font-bold text-slate-900 mb-2 text-sm">Where this topic is happening</h2>
          <div className="flex flex-wrap gap-2">
            {cat.top_places.map((p) => (
              <span key={p.label} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-xs">
                <MapPin className="w-3 h-3" /> {p.label} <span className="text-blue-500">· {p.count}</span>
              </span>
            ))}
          </div>
        </section>
      )}

      <section className="mt-10" data-testid="category-threads">
        <h2 className="font-bold text-slate-900 text-lg mb-3 inline-flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-blue-600" /> Latest in {cat.label}
        </h2>
        {threads.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center bg-slate-50">
            <p className="text-sm text-slate-600">No threads yet in {cat.label}.</p>
            <p className="text-xs text-slate-500 mt-1">Be the first to start a conversation.</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {threads.map((t) => (
              <li key={t.id}>
                <Link
                  to={`/thread/${t.id}`}
                  className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 bg-white hover:border-blue-300 hover:shadow-sm transition"
                  data-testid={`cat-thread-${t.id}`}
                >
                  <MessageCircle className="w-4 h-4 text-slate-400 shrink-0 mt-1" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 line-clamp-1">{t.title}</div>
                    {t.location && (
                      <div className="text-[11px] text-slate-500 mt-0.5 inline-flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {[t.location.city, t.location.state, t.location.country].filter(Boolean).join(", ")}
                      </div>
                    )}
                  </div>
                  <span className="text-[11px] text-slate-500 shrink-0">{t.post_count || 0} replies</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
